import { useState } from "react";
import { ChevronDown, ChevronUp, Info } from "lucide-react";

/**
 * CourseParDisplay - Displays course par information with Starting Nine toggle
 * Shows hole-by-hole pars and handicap ratings with toggle visibility
 */
const CourseParDisplay = ({
  selectedCourseData,
  numberOfHoles,
  startingNine,
  setStartingNine,
  getActualHoleNumber
}) => {
  const [showHandicaps, setShowHandicaps] = useState(false);
  
  if (!selectedCourseData) return null;

  const hasHandicaps = selectedCourseData.hole_handicaps && selectedCourseData.hole_handicaps.length > 0;

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 space-y-2" data-testid="course-par-display">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-bold text-green-800 text-sm">{selectedCourseData.name}</h4>
          {selectedCourseData.location && (
            <p className="text-xs text-green-600">{selectedCourseData.location}</p>
          )}
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold text-green-700">Par {selectedCourseData.par}</span>
        </div>
      </div>
      
      {/* Starting Nine Toggle - For 18-hole rounds on standard courses */}
      {numberOfHoles === 18 && !selectedCourseData.selected_nines && (
        <div className="pt-2 border-t border-green-200" data-testid="starting-nine-toggle">
          <p className="text-xs text-green-600 mb-2">Starting Nine:</p>
          <div className="flex gap-2">
            <button
              onClick={() => setStartingNine('front')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                startingNine === 'front'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-green-700 border border-green-300 hover:bg-green-50'
              }`}
            >
              Front 9 First
              <span className="block text-xs opacity-75">(Holes 1-9)</span>
            </button>
            <button
              onClick={() => setStartingNine('back')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                startingNine === 'back'
                  ? 'bg-green-600 text-white'
                  : 'bg-white text-green-700 border border-green-300 hover:bg-green-50'
              }`}
            >
              Back 9 First
              <span className="block text-xs opacity-75">(Holes 10-18)</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Hole-by-Hole Par Display */}
      {selectedCourseData.hole_pars && selectedCourseData.hole_pars.length > 0 && (
        <div className="pt-2 border-t border-green-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-green-600">Hole Pars</p>
            {hasHandicaps && (
              <button
                onClick={() => setShowHandicaps(!showHandicaps)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                data-testid="toggle-handicaps-btn"
              >
                {showHandicaps ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Hide Handicaps
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    Show Hole Handicaps
                  </>
                )}
              </button>
            )}
          </div>
          
          {/* 9-Hole Round Display */}
          {numberOfHoles === 9 && (
            <>
              {/* Warning if course doesn't have back 9 pars */}
              {startingNine === 'back' && selectedCourseData.hole_pars?.length < 18 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">
                  <p className="text-xs text-amber-700">
                    ⚠️ Back 9 pars not available for this course. Showing front 9 pars as reference.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-9 gap-1 mb-1">
                {/* For 9-hole rounds: show Front 9 (holes 1-9) or Back 9 (holes 10-18) based on startingNine */}
                {(() => {
                  const allPars = selectedCourseData.hole_pars || [];
                  let parsToShow;
                  
                  if (startingNine === 'front') {
                    // Front 9: show first 9 pars
                    parsToShow = allPars.slice(0, 9);
                  } else {
                    // Back 9: show pars 10-18 if available, otherwise show pars 1-9 as fallback
                    parsToShow = allPars.length >= 18 
                      ? allPars.slice(9, 18) 
                      : allPars.slice(0, 9);
                  }
                  
                  // If no pars, show defaults
                  if (parsToShow.length === 0) {
                    parsToShow = [4, 4, 4, 4, 4, 4, 4, 4, 4];
                  }
                  
                  return parsToShow.map((par, idx) => {
                    const holeNumber = startingNine === 'front' ? idx + 1 : idx + 10;
                    const handicapIdx = startingNine === 'front' ? idx : idx + 9;
                    return (
                      <div key={`par-${holeNumber}`} className="text-center">
                        <div className="text-[10px] text-green-500">{holeNumber}</div>
                        <div className="text-sm font-bold text-green-700">{par}</div>
                        {showHandicaps && hasHandicaps && (
                          <div className="text-[9px] text-blue-600 font-medium bg-blue-50 rounded px-1">
                            H{selectedCourseData.hole_handicaps?.[handicapIdx] || '-'}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </>
          )}
          
          {/* 18-Hole Round Display - First 9 Holes */}
          {numberOfHoles === 18 && (
            <>
              <div className="grid grid-cols-9 gap-1 mb-1">
                {selectedCourseData.hole_pars.slice(0, 9).map((par, idx) => (
                  <div key={`par-front-${idx}`} className="text-center">
                    <div className="text-[10px] text-green-500">{getActualHoleNumber(idx)}</div>
                    <div className="text-sm font-bold text-green-700">{par}</div>
                    {showHandicaps && hasHandicaps && (
                      <div className="text-[9px] text-blue-600 font-medium bg-blue-50 rounded px-1">
                        H{selectedCourseData.hole_handicaps[idx] || '-'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Second 9 Holes */}
              {selectedCourseData.hole_pars.length > 9 && (
                <div className="grid grid-cols-9 gap-1">
                  {selectedCourseData.hole_pars.slice(9, 18).map((par, idx) => (
                    <div key={`par-back-${idx}`} className="text-center">
                      <div className="text-[10px] text-green-500">{getActualHoleNumber(idx + 9)}</div>
                      <div className="text-sm font-bold text-green-700">{par}</div>
                      {showHandicaps && hasHandicaps && (
                        <div className="text-[9px] text-blue-600 font-medium bg-blue-50 rounded px-1">
                          H{selectedCourseData.hole_handicaps[idx + 9] || '-'}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* Handicap Info Box - Shows when handicaps are visible */}
      {showHandicaps && hasHandicaps && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2" data-testid="handicap-info-box">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700">
              <p className="font-medium mb-1">Handicap Stroke Allocation</p>
              <p>Lower numbers = harder holes. A player getting 5 strokes receives them on holes rated 1-5.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseParDisplay;
